<template>
    <section
        v-editable="blok"
        class="packages-overview"
    >
        <KSectionHeader
            v-if="title || blok.eyebrow || blok.text"
            class="packages-overview__header"
            :eyebrow="blok.eyebrow"
            :text="blok.text"
            :align="blok.align || 'start'"
        >
            <template
                v-if="title"
                #title
            >
                <span v-html="title" />
            </template>
            <template
                v-if="hasConfiguredAction || showCategoryLink"
                #actions
            >
                <StoryblokSectionHeaderAction
                    v-if="hasConfiguredAction"
                    :label="blok.action_label"
                    :link="blok.action_link"
                    placement="packages-overview"
                />
                <KButton
                    v-else
                    :as="nuxtLink"
                    :to="categoryUrl"
                    :aria-label="`View all ${categoryLabel} packages`"
                    variant="secondary"
                    @click="trackCategory"
                >
                    View all
                    <template #icon-end>
                        <Icon
                            name="tabler:arrow-right"
                            aria-hidden="true"
                        />
                    </template>
                </KButton>
            </template>
        </KSectionHeader>

        <p
            v-if="pending && !packages.length"
            class="packages-overview__status"
        >
            Loading packages...
        </p>
        <p
            v-else-if="error"
            class="packages-overview__status packages-overview__status--error"
        >
            Packages could not be loaded. Please try again later.
        </p>
        <p
            v-else-if="!packages.length"
            class="packages-overview__status"
        >
            No packages are available at the moment.
        </p>
        <div
            v-else
            class="packages-overview__grid"
        >
            <PackageCard
                v-for="packageData in packages"
                :key="packageData.uuid"
                :package-data="packageData"
                :detail="blok.detail"
                @select="trackPackage(packageData, $event)"
            />
        </div>
    </section>
</template>

<script setup>
import { KButton, KSectionHeader } from '@vemedia/karibu'
import { computed, resolveComponent } from 'vue'
import { PACKAGE_CATEGORIES } from '#shared/package-categories'
import PackageCard from '~/components/package-card/package-card.vue'
import renderHighlightedText from '~/helpers/renderHighlightedText'
import {
    categoryPath,
    isCurrentCategoryPage,
    packagesEndpoint,
    visiblePackages,
} from './overview-utils'

const { blok } = defineProps(['blok'])
const { capture } = useTracking()
const route = useRoute()
const nuxtLink = resolveComponent('NuxtLink')

const title = computed(() => {
    if (!blok.title?.content?.length)
        return ''
    const document = { ...blok.title, content: blok.title.content.map(node => ({ ...node })) }
    return renderHighlightedText(document)
})

const categoryUrl = computed(() => categoryPath(blok.category))
const categoryLabel = computed(() => PACKAGE_CATEGORIES[blok.category] || '')
const hasConfiguredAction = computed(() => Boolean(
    blok.action_label
    && (blok.action_link?.url
        || blok.action_link?.email
        || blok.action_link?.cached_url
        || blok.action_link?.anchor),
))
const showCategoryLink = computed(() => Boolean(
    categoryLabel.value
    && !isCurrentCategoryPage(route.path, blok.category),
))

const { data, error, pending } = await useAsyncData(
    `packages-overview-${blok._uid}-${blok.category || 'all'}`,
    () => $fetch(packagesEndpoint(blok.category)),
    { default: () => [] },
)

const packages = computed(() => visiblePackages(data.value || [], blok.max_items))

function trackPackage(packageData, action) {
    capture('package_overview_clicked', {
        placement: 'packages-overview',
        category: blok.category || 'all',
        target_slug: packageData.full_slug,
        action,
    })
}

function trackCategory() {
    capture('package_overview_clicked', {
        placement: 'packages-overview',
        category: blok.category,
        target_slug: categoryUrl.value.replace(/^\//, ''),
        action: 'view_all',
    })
}
</script>

<style lang="scss">
@use 'packages-overview';
</style>
